import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Plus, Send, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SEVERITIES = ["minor", "moderate", "serious"];
const TYPES = ["accident", "near-miss", "hazard", "illness"];

const EMPTY = {
  person_name: "", person_role: "", person_phone: "",
  incident_date: "", incident_time: "", incident_location: "",
  incident_type: "", incident_description: "",
  injury_body_part: "", injury_severity: "", treatment_given: "",
  treatment_by: "", hospital_attended: false,
  contributing_factors: "", corrective_actions: "",
  employee_signature: "",
};

export default function MyIncidents() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/incidents/mine");
      setRows(data.incidents || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/incidents", { form: EMPTY });
      setActive(data); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const openExisting = (row) => { setActive(row); setForm({ ...EMPTY, ...(row.form || {}) }); };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/incidents/${active.id}`, { form });
      setActive(data); toast.success("Saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/incidents/${active.id}`, { form });
      const { data } = await api.post(`/incidents/${active.id}/submit`);
      setActive(data); toast.success("Submitted to your manager"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const locked = active && !["DRAFT", "RETURNED"].includes(active.status);

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <h1 className="text-xl font-bold text-[#1A2744]">Accident / Incident records</h1>
      </div>

      {!active && (
        <>
          <Button onClick={startNew} disabled={busy} className="bg-[#1A2744] text-white" data-testid="incident-new">
            <Plus className="h-4 w-4 mr-1" /> New record
          </Button>
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Type</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-500 py-6">No records yet</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{r.form?.incident_date || r.created_at?.slice(0,10)}</TableCell>
                    <TableCell className="text-xs">{r.form?.incident_type || "—"}</TableCell>
                    <TableCell className="text-xs"><span className="px-2 py-0.5 rounded bg-gray-100 font-mono text-[10px]">{r.status}</span></TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => openExisting(r)}>Open</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}

      {active && (
        <Card><CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-bold text-[#1A2744]">Record · <span className="font-mono text-xs text-gray-500">{active.status}</span></h2>
            <Button variant="ghost" size="sm" onClick={() => setActive(null)}>Back to list</Button>
          </div>
          {active.manager_comments && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs">
              <strong>Manager comments:</strong> {active.manager_comments}
            </div>
          )}

          {[
            ["person_name", "Person involved (name) *", "input"],
            ["person_role", "Their role", "input"],
            ["person_phone", "Phone", "input"],
            ["incident_date", "Date *", "date"],
            ["incident_time", "Time", "time"],
            ["incident_location", "Location (e.g. behind till)", "input"],
            ["incident_type", "Type *", "select-type"],
            ["incident_description", "What happened? *", "textarea"],
            ["injury_body_part", "Injury · body part", "input"],
            ["injury_severity", "Severity", "select-sev"],
            ["treatment_given", "Treatment given", "textarea"],
            ["treatment_by", "Treated by", "input"],
            ["contributing_factors", "Contributing factors", "textarea"],
            ["corrective_actions", "Corrective actions", "textarea"],
            ["employee_signature", "Your name (signature) *", "input"],
          ].map(([k, label, kind]) => (
            <div key={k}>
              <Label>{label}</Label>
              {kind === "textarea" && <Textarea rows={3} value={form[k] || ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`f-${k}`} />}
              {kind === "select-type" && (
                <select className="w-full border rounded h-9 px-2 text-sm" value={form[k] || ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`f-${k}`}>
                  <option value="">— select —</option>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
              {kind === "select-sev" && (
                <select className="w-full border rounded h-9 px-2 text-sm" value={form[k] || ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`f-${k}`}>
                  <option value="">— select —</option>
                  {SEVERITIES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
              {(kind === "input" || kind === "date" || kind === "time") && (
                <Input type={kind === "input" ? "text" : kind} value={form[k] || ""} disabled={locked} onChange={(e) => upd(k, e.target.value)} data-testid={`f-${k}`} />
              )}
            </div>
          ))}

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.hospital_attended} disabled={locked} onChange={(e) => upd("hospital_attended", e.target.checked)} />
            Hospital / medical centre attended
          </label>

          {!locked && (
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={save} disabled={busy} data-testid="incident-save"><Save className="h-3.5 w-3.5 mr-1" /> Save draft</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#1A2744] text-white" data-testid="incident-submit"><Send className="h-3.5 w-3.5 mr-1" /> Submit to manager</Button>
            </div>
          )}
          {locked && <p className="text-xs text-gray-500 italic">This record is {active.status.toLowerCase()} — read-only.</p>}
        </CardContent></Card>
      )}
    </div>
  );
}
